import { QueryClient, type QueryKey } from '@tanstack/react-query';
import { isApiError } from '@/lib/api/client';

/**
 * Shared TanStack Query configuration.
 *
 * Availability data changes often and is authoritative on the server, so we use
 * a short stale window plus realtime invalidation instead of aggressive caching.
 */
const MINUTE = 60_000;

const shouldRetry = (failureCount: number, error: unknown): boolean => {
  // Never retry a rejection the user caused (validation, 403, 404, 409...).
  if (isApiError(error) && error.isClientError) return false;
  return failureCount < 2;
};

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: MINUTE,
        gcTime: 10 * MINUTE,
        retry: shouldRetry,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        throwOnError: false,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

/* -------------------------------------------------------------------------- */
/* Query keys                                                                  */
/* -------------------------------------------------------------------------- */

export interface CalendarRangeKey {
  start: string;
  end: string;
  roomIds?: readonly number[];
  search?: string;
}

/**
 * Centralized cache keys. Every feature must build keys from here so realtime
 * events and mutations can invalidate precisely (and never guess a string).
 */
export const queryKeys = {
  session: () => ['session', 'me'] as const,
  health: () => ['health'] as const,

  rooms: {
    all: () => ['rooms'] as const,
    list: (filters?: Record<string, unknown>) => ['rooms', 'list', filters ?? {}] as const,
    detail: (roomId: number) => ['rooms', 'detail', roomId] as const,
    features: () => ['rooms', 'features'] as const,
    blocks: (filters?: Record<string, unknown>) => ['rooms', 'blocks', filters ?? {}] as const,
  },

  calendar: {
    all: () => ['calendar'] as const,
    range: (range: CalendarRangeKey) => ['calendar', 'range', range] as const,
    availability: (filters: Record<string, unknown>) => ['calendar', 'availability', filters] as const,
  },

  /** The signed-in person's own Outlook calendar; never anybody else's. */
  myMeetings: {
    all: () => ['my-meetings'] as const,
    list: (filters?: Record<string, unknown>) => ['my-meetings', 'list', filters ?? {}] as const,
  },

  reservations: {
    all: () => ['reservations'] as const,
    mine: (filters?: Record<string, unknown>) => ['reservations', 'mine', filters ?? {}] as const,
    detail: (reservationId: number) => ['reservations', 'detail', reservationId] as const,
  },

  users: {
    all: () => ['users'] as const,
    list: (filters?: Record<string, unknown>) => ['users', 'list', filters ?? {}] as const,
    detail: (userId: number) => ['users', 'detail', userId] as const,
  },

  audit: {
    all: () => ['audit'] as const,
    list: (filters?: Record<string, unknown>) => ['audit', 'list', filters ?? {}] as const,
  },

  calendarSync: {
    all: () => ['calendar-sync'] as const,
    status: () => ['calendar-sync', 'status'] as const,
  },
} as const;

/** Key groups invalidated when the realtime channel reports a change. */
export const invalidationGroups = {
  reservationChanged: (): QueryKey[] => [
    queryKeys.calendar.all(),
    queryKeys.reservations.all(),
    queryKeys.rooms.all(),
  ],
  roomChanged: (): QueryKey[] => [queryKeys.rooms.all(), queryKeys.calendar.all()],
  availabilityChanged: (): QueryKey[] => [queryKeys.calendar.all(), queryKeys.rooms.all()],
} as const;
